import React, { useCallback, useContext, useEffect, useState } from "react"
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from "@mui/material"
import { BoxFC } from "components/BoxCustom"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { alertSuccess } from "components/Alert"
import PagePermissionGrid from "pages/Menu6/PermissionGroupManagement/PagePermissionGrid"
import useCompactLayout, { usePhoneLayout } from "hooks/useCompactLayout"
import { CloseRounded } from "resource/muiImport"

/**
 * Per-account page-permission overrides on top of group permissions.
 * A page left as "ตามกลุ่ม" (no override row) inherits from groups.
 */
const AccountPagePermissionDialog = ({ AccId, dialogOpen, setDialogOpen }) => {

  const { ax } = useContext(GlobalStateContext)
  const [pages, setPages] = useState([])
  const [values, setValues] = useState({})
  const [groupPms, setGroupPms] = useState({})
  const isCompact = useCompactLayout()
  const isPhone = usePhoneLayout()

  const handleSave = useCallback(() => {
    const postData = {
      AccId: AccId,
      Overrides: Object.keys(values).map(pageCd => ({ PageCd: pageCd, Mode: values[pageCd] })),
    }
    ax.post("/permission-group/updateAccountPagePermission", postData).then(value => {
      if (value.data) {
        alertSuccess("บันทึกข้อมูลเรียบร้อยแล้ว")
        setDialogOpen(false)
      }
    })
  }, [ax, AccId, values, setDialogOpen])

  useEffect(() => {
    if (dialogOpen) {
      ax.post("/permission-group/getPages", {}).then(value => {
        if (value.data) setPages(value.data)
      })
      ax.post("/permission-group/getAccountPermissionInfo", { AccId: AccId }).then(value => {
        if (value.data) {
          setGroupPms(value.data.GroupPms || {})
          setValues((value.data.Overrides || []).reduce((obj, r) => ({ ...obj, [r.PageCd]: r.Mode }), {}))
        }
      })
    } else {
      setValues({})
      setGroupPms({})
    }
  }, [dialogOpen, AccId, ax])

  const hint = (
    <Typography variant="body2" color="text.secondary">
      "ตามกลุ่ม" = ใช้สิทธิ์จากกลุ่มสิทธิ์ / เลือกค่าอื่นเพื่อกำหนดสิทธิ์เฉพาะผู้ใช้งานคนนี้ (ชนะสิทธิ์จากกลุ่มเสมอ)
    </Typography>
  )

  if (isCompact) {
    // phones: full screen with the grid filling the height, tablets: plain (non-draggable) dialog
    return (
      <Dialog open={dialogOpen} fullWidth maxWidth="md" fullScreen={isPhone}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="account-page-pm-title">
        <Box sx={{
          display: "flex", alignItems: "center", gap: 0.5, px: 0.5, pb: 0.5, flexShrink: 0,
          pt: isPhone ? "calc(4px + env(safe-area-inset-top))" : 0.5, bgcolor: "primary.main", color: "primary.contrastText"
        }}>
          <IconButton color="inherit" aria-label="ปิด" onClick={() => setDialogOpen(false)}><CloseRounded /></IconButton>
          <Typography id="account-page-pm-title" noWrap sx={{ flex: 1, fontWeight: 700, fontSize: 17 }}>
            กำหนดสิทธิ์รายหน้า (Override)
          </Typography>
        </Box>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1, pt: "12px !important", px: isPhone ? 1 : 3 }}>
          {hint}
          {isPhone ?
            <Box flex={1} minHeight={0}>
              <PagePermissionGrid pages={pages} values={values} setValues={setValues}
                withInherit effective={groupPms} compact height="100%" />
            </Box>
            :
            <PagePermissionGrid pages={pages} values={values} setValues={setValues}
              withInherit effective={groupPms} height="60vh" />
          }
        </DialogContent>
        <Box sx={{
          display: "flex", gap: 1, px: 1, pt: 1, flexShrink: 0,
          pb: isPhone ? "calc(8px + env(safe-area-inset-bottom))" : 1,
          bgcolor: "background.paper", borderTop: 1, borderColor: "divider"
        }}>
          <Button variant="outlined" color="inherit" sx={{ flex: 1, minHeight: 48 }} onClick={() => setDialogOpen(false)}>ยกเลิก</Button>
          <Button variant="contained" sx={{ flex: 2, minHeight: 48 }} onClick={handleSave}>บันทึก</Button>
        </Box>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='md'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title="กำหนดสิทธิ์รายหน้า (Override)"
        onDeleteClick={null}
      />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1 }}>
          {hint}
          <PagePermissionGrid pages={pages} values={values} setValues={setValues}
            withInherit effective={groupPms} />
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="บันทึก"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleSave} />
    </Dialog>
  )
}

export default React.memo(AccountPagePermissionDialog)
